import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import Carousel from 'react-slick';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import type { StaticImageData } from 'Components/Image';
import DesktopCarousel from 'Components/Slider';
import { Typography } from 'Components/Typography';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const IngredientsLayout = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  margin-bottom: ${legacyTheme.spacing.s24};

  width: 320px;

  ${legacyTheme.breakpoints.up('sm')} {
    width: 624px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    width: 500px;
  }
`;

const IngredientCard = styled.div<{ ingredientsLength: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${({ ingredientsLength }) => (ingredientsLength > 4 ? '33%' : '50%')};
  min-width: 100px;
  margin-top: ${legacyTheme.spacing.s16};

  ${legacyTheme.breakpoints.up('md')} {
    width: 33%;
  }
`;

const IngredientImage = styled.img`
  height: 65px;
`;

const IngredientName = styled(Typography)`
  margin-bottom: 4px;
`;
const StyledTitle = styled(Typography)<{ $color?: string }>`
  color: ${({ $color }) => $color};
  max-width: 320px;

  ${legacyTheme.breakpoints.up('md')} {
    max-width: auto;
  }
`;

type IngredientsSlideProps = {
  ingredients: {
    image: StaticImageData;
    name: string;
    benefit: string;
  }[];
  title?: string;
  color?: string;
};

const IngredientsSlide = ({
  ingredients,
  title,
  color = theme.colors.neutral[800],
}: IngredientsSlideProps) => {
  return (
    <Root data-click="change-slide" data-from="formula-module">
      {title && (
        <StyledTitle $color={color} markupName="h3" paragraph variant="mono2">
          {title}
        </StyledTitle>
      )}

      <IngredientsLayout>
        {ingredients.map((ingredient, i) => (
          <IngredientCard key={String(i)} ingredientsLength={ingredients.length}>
            <IngredientImage
              alt={ingredient.name}
              // @todo : remove condition for src
              src={ingredient.image?.src ?? ingredient.image}
            />
            <IngredientName color="grey" variant="p3">
              {ingredient.name}
            </IngredientName>

            <Typography color="grey" variant="p4">
              {ingredient.benefit}
            </Typography>
          </IngredientCard>
        ))}
      </IngredientsLayout>
    </Root>
  );
};

const useStyles = makeStyles({
  slider: {
    marginTop: legacyTheme.spacing.s24,
    marginBottom: legacyTheme.spacing.s40,
  },
});

const MobileCarousel = styled(Carousel)`
  width: 320px;

  margin: 24px auto 40px;

  .slick-prev {
    z-index: 1;
    left: 10px;

    ::before {
      color: black;
    }
  }

  .slick-next {
    right: 20px;

    ::before {
      color: black;
    }
  }

  .slick-list {
    height: 320px;
  }

  .slick-dots {
    bottom: -10px;
    left: -3px;

    li {
      width: 12px;
      margin: 0;

      button {
        width: 12px;
      }

      button::before {
        width: 12px;
        font-size: 7px;
        opacity: 0.4;
      }
    }
  }

  ${legacyTheme.breakpoints.up('sm')} {
    width: 624px;
    height: 320px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    .slick-prev {
      left: -10px;
    }
  }

  ${legacyTheme.breakpoints.up('lg')} {
    width: 90%;
    margin-left: 50px;
  }
`;

type IngredientsSliderProps = {
  slides: {
    title?: string;
    ingredients: {
      image: StaticImageData;
      name: string;
      benefit: string;
    }[];
  }[];
  TitleProps?: {
    color?: string;
  };
};

export const IngredientsSlider = ({ slides, TitleProps }: IngredientsSliderProps) => {
  const { classes } = useStyles();
  const { isDesktop } = useResponsiveVariant();
  return !isDesktop ? (
    <MobileCarousel
      arrows={false}
      dots
      focusOnSelect
      infinite
      slidesToScroll={1}
      slidesToShow={1}
      speed={500}
      variableWidth
    >
      {slides.map(({ title, ingredients }, key) => (
        <IngredientsSlide
          key={String(key)}
          color={TitleProps?.color}
          ingredients={ingredients}
          title={title}
        />
      ))}
    </MobileCarousel>
  ) : (
    <DesktopCarousel
      classes={{ root: classes.slider }}
      controlsTheme="noir"
      dataFrom="formula-module"
    >
      {slides.map(({ title, ingredients }, key) => (
        <IngredientsSlide
          key={String(key)}
          color={TitleProps?.color}
          ingredients={ingredients}
          title={title}
        />
      ))}
    </DesktopCarousel>
  );
};
